import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, BarChart2, TrendingUp, Activity, Shield, Building2, Users, MapPin, CheckCircle, Zap } from "lucide-react";
import { MarketingNav, MarketingFooter } from "./MarketingLayout";
import "../../marketing.css";

const MODULES = [
  { icon: BarChart2, name: "Enterprise Dashboard", desc: "Command-level overview: fatigue index, roster compliance, ground ops health and flight ops status — all on one screen.", badge: "COMMAND", metric: "Live", color: "text-violet-400 bg-violet-500/10 border-violet-500/20" },
  { icon: Activity, name: "Safety Analytics", desc: "SMS incident trends, risk matrix heat maps, precursor event frequency and DGCA occurrence classification reports.", badge: "SMS", metric: "DGCA", color: "text-red-400 bg-red-500/10 border-red-500/20" },
  { icon: MapPin, name: "Station Performance", desc: "Per-station OTP, turnaround compliance, delay cause analysis and ground task completion rates across all bases.", badge: "MULTI-BASE", metric: "All", color: "text-orange-400 bg-orange-500/10 border-orange-500/20" },
  { icon: TrendingUp, name: "Fatigue Risk Trends", desc: "7-day and 28-day fatigue score trend lines per crew member, department and base. Identify chronic fatigue patterns early.", badge: "PREDICTIVE", metric: "28d", color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  { icon: Building2, name: "Back Office Reports", desc: "Crew utilisation, FDP pattern analysis, cost-per-rotation reporting and duty summary exports for payroll integration.", badge: "REPORTS", metric: "PDF", color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  { icon: Shield, name: "Vendor SLA Analytics", desc: "Ground handler SLA performance: task completion rate, response time, exception trends and contract compliance scoring.", badge: "SLA", metric: "Live", color: "text-green-400 bg-green-500/10 border-green-500/20" },
];

const DASHBOARD_METRICS = [
  { label: "Fleet Fatigue Index", value: "2.4", unit: "/10", trend: "+0.2", trendDown: false },
  { label: "Roster Compliance", value: "98.1", unit: "%", trend: "+1.4%", trendDown: false },
  { label: "OTP (On-Time Perf.)", value: "87.3", unit: "%", trend: "-1.2%", trendDown: true },
  { label: "Open SMS Items", value: "3", unit: "", trend: "-5", trendDown: false },
];

export default function AnalyticsPage() {
  return (
    <div className="min-h-screen bg-[#07111f] text-white marketing-font-body">
      <MarketingNav />

      {/* ── HERO ── */}
      <section className="pt-28 pb-20 relative overflow-hidden grid-texture">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 50% at 60% 30%, rgba(139,92,246,0.08) 0%, transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/20 px-3 py-1.5 rounded mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-400 live-dot" />
              <span className="text-[10px] text-violet-400 marketing-font-mono uppercase font-bold tracking-widest">Analytics & Intelligence</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-black marketing-font-heading leading-tight mb-5">
              Command-level intelligence<br />
              <span className="text-violet-400">across every domain.</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed mb-8 max-w-2xl">
              From individual crew fatigue scores to fleet-wide OTP trends — analytics that surface the operational picture before a problem becomes a disruption.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/marketing/demo" className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5" data-testid="analytics-demo-btn">
                Request Demo <ArrowRight size={16} />
              </Link>
              <Link to="/login" className="flex items-center gap-2 border border-white/15 hover:border-white/30 text-slate-300 px-7 py-3.5 transition-all">
                See Live Dashboard <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── LIVE DASHBOARD MOCKUP ── */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="bg-[#0c1929]/60 border border-violet-500/10 p-6 rounded-sm">
            <div className="flex items-center gap-2 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-400 live-dot" />
              <span className="text-[10px] text-violet-400 marketing-font-mono uppercase tracking-widest font-bold">Enterprise Dashboard — Live Preview</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              {DASHBOARD_METRICS.map((m, i) => (
                <div key={i} className="bg-[#07111f] border border-white/5 p-4">
                  <div className="text-[9px] text-slate-600 marketing-font-mono uppercase tracking-widest mb-2">{m.label}</div>
                  <div className="flex items-end gap-1 mb-1">
                    <span className="text-2xl font-black text-white marketing-font-mono">{m.value}</span>
                    <span className="text-sm text-slate-600 marketing-font-mono mb-0.5">{m.unit}</span>
                  </div>
                  <div className={`text-[10px] marketing-font-mono font-bold ${m.trendDown ? "text-red-400" : "text-green-400"}`}>{m.trend} vs last week</div>
                </div>
              ))}
            </div>
            {/* Fake bar chart */}
            <div className="bg-[#07111f] border border-white/5 p-4">
              <div className="text-[9px] text-slate-600 marketing-font-mono uppercase tracking-widest mb-3">Crew Fatigue Index — Last 14 Days</div>
              <div className="flex items-end gap-1.5 h-16">
                {[3.1,2.8,3.4,2.9,2.7,3.1,2.4,3.8,2.6,2.4,2.9,2.5,2.4,2.2].map((v, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full rounded-sm bg-violet-500/30 hover:bg-violet-500/50 transition-colors" style={{ height: `${(v / 4) * 100}%` }} />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[8px] text-slate-700 marketing-font-mono">14d ago</span>
                <span className="text-[8px] text-slate-700 marketing-font-mono">Today</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6 MODULES ── */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-10">
            <div className="text-[10px] text-violet-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">Analytics suite</div>
            <h2 className="text-3xl font-black marketing-font-heading">Six intelligence modules. One data layer.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={i} className={`bg-[#0c1929]/50 border ${m.color.split(" ").find(c => c.startsWith("border"))} hover:border-opacity-60 p-6 card-glow transition-all`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-9 h-9 ${m.color.split(" ").find(c => c.startsWith("bg"))} border ${m.color.split(" ").find(c => c.startsWith("border"))} flex items-center justify-center`}>
                      <Icon size={15} className={m.color.split(" ")[0]} />
                    </div>
                    <div>
                      <div className={`text-[9px] marketing-font-mono font-bold uppercase tracking-widest ${m.color.split(" ")[0]}`}>{m.badge}</div>
                      <div className="text-sm font-bold text-white marketing-font-heading">{m.name}</div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── KEY FEATURES ── */}
      <section className="py-20 border-t border-white/5 bg-[#04090f] grid-texture">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: "Real-time. Always.", desc: "Every metric updates live as data flows from crew ops, ground ops and flight ops. No manual report runs.", color: "text-orange-400 bg-orange-500/10 border-orange-500/20" },
              { icon: TrendingUp, title: "Trend detection.", desc: "Pattern analysis surfaces recurring fatigue spikes, SLA underperformers and safety precursors before they escalate.", color: "text-violet-400 bg-violet-500/10 border-violet-500/20" },
              { icon: CheckCircle, title: "Export-ready.", desc: "Every dashboard exports to PDF or CSV for DGCA audits, board reporting and vendor review meetings.", color: "text-green-400 bg-green-500/10 border-green-500/20" },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className={`border ${item.color.split(" ").find(c => c.startsWith("border"))} bg-[#0c1929]/40 p-6`}>
                  <div className={`w-10 h-10 ${item.color.split(" ").find(c => c.startsWith("bg"))} border ${item.color.split(" ").find(c => c.startsWith("border"))} flex items-center justify-center mb-4`}>
                    <Icon size={18} className={item.color.split(" ")[0]} />
                  </div>
                  <div className="font-bold text-white mb-2">{item.title}</div>
                  <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 border-t border-white/5 bg-[#04090f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-3xl font-black marketing-font-heading mb-4">See your airline's data, live.</h2>
          <p className="text-slate-400 max-w-md mx-auto mb-8">We configure the demo with your fleet profile so the dashboards are meaningful from the first screen.</p>
          <Link to="/marketing/demo" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-9 py-4 transition-all hover:-translate-y-0.5 shadow-xl shadow-orange-500/20">
            Request Demo <ArrowRight size={17} />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
